import { 
  getFirestore, 
  doc, 
  setDoc, 
  serverTimestamp, 
  getDoc, 
  collection, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy,
  limit,
  writeBatch,
  getCountFromServer,
  where,
  startAfter,
  arrayUnion,
  increment
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { db, teachClassDB } from '../shared/api/firebase';

// Firebase 초기화
const app = getApp();

// 학급당 최대 학생 수 제한
export const MAX_STUDENTS_PER_CLASS = 50;

// 랜덤 룸 코드 생성 함수
export const generateRoomCode = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
};

// 토론 정보 가져오기
export const fetchDebateInfo = async (roomCode) => {
  try {
    const docRef = doc(db, "debate_group", roomCode);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const debateData = docSnap.data();
      console.log('🔍 [firebaseService.fetchDebateInfo] Firebase 원본 데이터:', debateData);
      return {
        success: true,
        data: {
          topic: debateData.title || debateData.topic,
          participantCount: debateData.participantCount ?? 20, // 참여 인원수 추가 (0도 유효한 값)
          currentRound: debateData.currentRound,
          debateMode: debateData.debateMode || 'refute' // 토론 모드 추가 (기본값: 'refute')
        }
      };
    }
    
    return { success: false, error: '토론 데이터를 찾을 수 없습니다.' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 새로운 통합 함수: 토론 정보와 모든 주장을 한 번에 가져오기
export const fetchDebateData = async (roomCode) => {
  try {
    const debateDocRef = doc(db, "debate_group", roomCode);
    const proCollectionRef = collection(db, "debate_group", roomCode, "pro");
    const conCollectionRef = collection(db, "debate_group", roomCode, "con");

    // Promise.all을 사용하여 데이터 병렬로 요청
    const [debateDocSnap, proSnapshot, conSnapshot] = await Promise.all([
      getDoc(debateDocRef),
      getDocs(proCollectionRef),
      getDocs(conCollectionRef)
    ]);

    // 토론 정보 처리
    if (!debateDocSnap.exists()) {
      return { success: false, error: '토론 데이터를 찾을 수 없습니다.' };
    }
    const debateData = debateDocSnap.data();

    // 찬성 주장 처리
    const proArguments = [];
    proSnapshot.forEach((doc) => {
      const data = doc.data();
      if (!data.isInitialDoc && data.documentType === 'argument' && data.content && 
          data.content.trim() !== "" && 
          data.content.trim() !== "새로운 주장") {
        proArguments.push({ id: doc.id, position: '찬성', ...data });
      }
    });

    // 반대 주장 처리
    const conArguments = [];
    conSnapshot.forEach((doc) => {
      const data = doc.data();
      if (!data.isInitialDoc && data.documentType === 'argument' && data.content && 
          data.content.trim() !== "" && 
          data.content.trim() !== "새로운 주장") {
        conArguments.push({ id: doc.id, position: '반대', ...data });
      }
    });
    
    const allArguments = [...proArguments, ...conArguments]
      .sort((a, b) => new Date(a.timestamp?.toDate()) - new Date(b.timestamp?.toDate()));

    return {
      success: true,
      data: {
        topic: debateData.title || debateData.topic,
        participantCount: debateData.participantCount ?? 20, // 0도 유효한 값
        currentRound: debateData.currentRound,
        arguments: allArguments,
        classId: debateData.classId || null // classId 추가
      }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 모든 주장 가져오기
export const fetchAllArguments = async (roomCode) => {
  try {
    const proCollectionRef = collection(db, "debate_group", roomCode, "pro");
    const proSnapshot = await getDocs(proCollectionRef);
    const proArguments = [];
    
    proSnapshot.forEach((doc) => {
      const data = doc.data();
      // 🔑 주장 문서만 처리하고, content가 유효한 경우만 추가
      if (!data.isInitialDoc && data.documentType === 'argument' && data.content && 
          data.content.trim() !== "" && 
          data.content.trim() !== "새로운 주장") {
        proArguments.push({
          id: doc.id,
          position: '찬성',
          content: data.content,
          timestamp: data.timestamp?.toDate().toISOString() || new Date().toISOString(),
          isAi: data.isAi || false,
          confidenceScore: data.confidenceScore || null,
          detailedScores: data.detailedScores || null,
          analysisResult: data.analysisResult || null,
          analysisComplete: data.analysisComplete || false,
          round: data.round || 1
        });
      }
    });
    
    const conCollectionRef = collection(db, "debate_group", roomCode, "con");
    const conSnapshot = await getDocs(conCollectionRef);
    const conArguments = [];
    
    conSnapshot.forEach((doc) => {
      const data = doc.data();
      // 🔑 주장 문서만 처리하고, content가 유효한 경우만 추가
      if (!data.isInitialDoc && data.documentType === 'argument' && data.content && 
          data.content.trim() !== "" && 
          data.content.trim() !== "새로운 주장") {
        conArguments.push({
          id: doc.id,
          position: '반대',
          content: data.content,
          timestamp: data.timestamp?.toDate().toISOString() || new Date().toISOString(),
          isAi: data.isAi || false,
          confidenceScore: data.confidenceScore || null,
          detailedScores: data.detailedScores || null,
          analysisResult: data.analysisResult || null,
          analysisComplete: data.analysisComplete || false,
          round: data.round || 1
        });
      }
    });
    
    const allArguments = [...proArguments, ...conArguments]
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    return {
      success: true,
      data: {
        arguments: allArguments
      }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 새 토론 생성
export const createNewDebate = async (topic, participantCount = 20, classId = null, classStudents = [], userId = null, debateMode = 'refute') => {
  try {
    const newRoomCode = generateRoomCode();
    
    const docRef = doc(db, "debate_group", newRoomCode);
    await setDoc(docRef, {
      title: topic,
      participantCount: participantCount, // 참여 인원수 추가
      classId: classId, // 학급 ID 추가
      classStudents: classStudents, // 학생 명단 추가
      createdBy: userId, // 🔒 생성자 ID 추가 (Phase 2 보안)
      debateMode: debateMode, // 토론 모드 추가 ('original' 또는 'refute')
      createdAt: serverTimestamp(),
      debateHistory: [],
      currentRound: 1
    });

    // 찬성/반대 컬렉션 초기화
    await setDoc(doc(db, "debate_group", newRoomCode, "pro", "init"), {
      isInitialDoc: true,
      createdAt: serverTimestamp()
    });
    
    await setDoc(doc(db, "debate_group", newRoomCode, "con", "init"), {
      isInitialDoc: true,
      createdAt: serverTimestamp()
    });

    return { success: true, roomCode: newRoomCode };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 라운드 정보 업데이트
export const updateRoundInfo = async (roomCode, roundNumber) => {
  try {
    const docRef = doc(db, "debate_group", roomCode);
    await updateDoc(docRef, {
      currentRound: roundNumber,
      roundChangeTime: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 분석 결과 업데이트
export const updateAnalysisResult = async (roomCode, argumentId, position, analysisData) => {
  try {
    const collectionName = position === '찬성' ? 'pro' : 'con';
    const docRef = doc(db, "debate_group", roomCode, collectionName, argumentId);
    
    await updateDoc(docRef, {
      confidenceScore: analysisData.confidenceScore,
      detailedScores: analysisData.detailedScores,
      needsAnalysis: false,
      analysisComplete: true,
      analysisResult: analysisData.analysisResult,
      analysisTimestamp: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 저장된 분석 결과 가져오기
export const getSavedAnalysisResult = async (roomCode, argumentId, position) => {
  try {
    const collectionName = position === '찬성' ? 'pro' : 'con';
    const docRef = doc(db, "debate_group", roomCode, collectionName, argumentId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists() && docSnap.data().analysisResult) {
      return {
        success: true,
        analysisResult: docSnap.data().analysisResult
      };
    }
    
    return { success: false };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 2라운드 시작 상태 업데이트
export const updateRound2StartStatus = async (roomCode, isStarted) => {
  try {
    const docRef = doc(db, "debate_group", roomCode);
    await updateDoc(docRef, {
      round2Started: isStarted,
      round2StartTime: isStarted ? serverTimestamp() : null
    });
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 3라운드 시작 상태 업데이트
export const updateRound3StartStatus = async (roomCode, isStarted) => {
  try {
    const docRef = doc(db, "debate_group", roomCode);
    await updateDoc(docRef, {
      round3Started: isStarted,
      round3StartTime: isStarted ? serverTimestamp() : null
    });
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 2라운드 시작 상태 가져오기
export const getRound2StartStatus = async (roomCode) => {
  try {
    const docRef = doc(db, "debate_group", roomCode);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        success: true,
        round2Started: data.round2Started || false
      };
    }
    
    return { success: false, error: '토론 데이터를 찾을 수 없습니다.' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 새로운 통합 구조: 닉네임 문서에 주장 배열로 저장
export const addArgumentToStudent = async (roomCode, nickname, position, argumentData) => {
  try {
    const collectionName = position === '찬성' ? 'pro' : 'con';
    const studentDocRef = doc(db, "debate_group", roomCode, collectionName, nickname);
    
    // 학생 문서 확인
    const studentDoc = await getDoc(studentDocRef);
    
    if (studentDoc.exists()) {
      const existingData = studentDoc.data();
      const existingArguments = existingData.arguments || [];
      
      // 새 주장을 배열에 추가 (serverTimestamp는 배열 안에서 사용 불가)
      const newArgument = {
        id: Date.now().toString(), // 고유 ID 생성
        round: argumentData.round,
        content: argumentData.content,
        timestamp: argumentData.timestamp || new Date(),
        needsAnalysis: true,
        analysisComplete: false,
        analysisResult: null,
        confidenceScore: null,
        detailedScores: null
      };
      
      const updatedArguments = [...existingArguments, newArgument];
      
      await updateDoc(studentDocRef, {
        arguments: updatedArguments,
        totalArguments: updatedArguments.length,
        lastSubmissionRound: argumentData.round,
        lastSubmissionTime: serverTimestamp(),
        hasSubmittedArgument: true
      });
      
      return { success: true, argumentId: newArgument.id };
    } else {
      // 학생 문서가 없으면 새로 생성
      const newArgument = {
        id: Date.now().toString(),
        round: argumentData.round,
        content: argumentData.content,
        timestamp: argumentData.timestamp || new Date(),
        needsAnalysis: true,
        analysisComplete: false,
        analysisResult: null,
        confidenceScore: null,
        detailedScores: null
      };
      
      const studentData = {
        documentType: 'student',
        nickname: nickname,
        position: position,
        joinedAt: serverTimestamp(),
        isActive: true,
        arguments: [newArgument],
        totalArguments: 1,
        lastSubmissionRound: argumentData.round,
        lastSubmissionTime: serverTimestamp(),
        hasSubmittedArgument: true
      };
      
      await setDoc(studentDocRef, studentData);
      
      return { success: true, argumentId: newArgument.id };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 통합 구조에서 AI 분석 결과 업데이트
export const updateArgumentAnalysis = async (roomCode, nickname, position, argumentId, analysisData) => {
  try {
    const collectionName = position === '찬성' ? 'pro' : 'con';
    const studentDocRef = doc(db, "debate_group", roomCode, collectionName, nickname);
    
    const studentDoc = await getDoc(studentDocRef);
    
    if (studentDoc.exists()) {
      const existingData = studentDoc.data();
      const studentArguments = existingData.arguments || [];
      
              // 해당 주장 찾아서 분석 결과 업데이트
        const updatedArguments = studentArguments.map(arg => {
        if (arg.id === argumentId) {
          return {
            ...arg,
            analysisResult: analysisData.analysisResult,
            confidenceScore: analysisData.confidenceScore,
            detailedScores: analysisData.detailedScores,
            needsAnalysis: false,
            analysisComplete: true,
            analysisTimestamp: new Date()
          };
        }
        return arg;
      });
      
      await updateDoc(studentDocRef, {
        arguments: updatedArguments
      });
      
      return { success: true };
    }
    
    return { success: false, error: '학생 문서를 찾을 수 없습니다.' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 학생의 특정 라운드 주장 가져오기
export const getStudentArgument = async (roomCode, nickname, position, round) => {
  try {
    const collectionName = position === '찬성' ? 'pro' : 'con';
    const studentDocRef = doc(db, "debate_group", roomCode, collectionName, nickname);
    
    const studentDoc = await getDoc(studentDocRef);
    
    if (studentDoc.exists()) {
      const data = studentDoc.data();
      const studentArguments = data.arguments || [];
      
      const argument = studentArguments.find(arg => arg.round === round);
      
      if (argument) {
        return {
          success: true,
          argument: {
            ...argument,
            nickname: nickname,
            position: position
          }
        };
      }
    }
    
    return { success: false, error: '주장을 찾을 수 없습니다.' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 모든 학생의 주장들을 통합 구조에서 가져오기
export const fetchAllArgumentsFromStudents = async (roomCode) => {
  try {
    const proCollectionRef = collection(db, "debate_group", roomCode, "pro");
    const conCollectionRef = collection(db, "debate_group", roomCode, "con");
    
    const [proSnapshot, conSnapshot] = await Promise.all([
      getDocs(proCollectionRef),
      getDocs(conCollectionRef)
    ]);
    
    const allArguments = [];
    
    // 찬성측 주장들 처리
    proSnapshot.forEach((doc) => {
      const data = doc.data();

      
      if (data.documentType === 'student' && data.arguments) {
        data.arguments.forEach(arg => {

          
          allArguments.push({
            ...arg,
            confidenceScore: arg.confidenceScore || null,
            detailedScores: arg.detailedScores || null,
            nickname: data.nickname,
            position: '찬성',
            studentId: data.studentId
          });
        });
      }
    });
    
    // 반대측 주장들 처리
    conSnapshot.forEach((doc) => {
      const data = doc.data();

      
      if (data.documentType === 'student' && data.arguments) {
        data.arguments.forEach(arg => {

          
          allArguments.push({
            ...arg,
            confidenceScore: arg.confidenceScore || null,
            detailedScores: arg.detailedScores || null,
            nickname: data.nickname,
            position: '반대',
            studentId: data.studentId
          });
        });
      }
    });
    
    // 시간순 정렬
    allArguments.sort((a, b) => {
      const aTime = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
      const bTime = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
      return aTime - bTime;
    });
    
    return {
      success: true,
      data: {
        arguments: allArguments
      }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== MARBLE DRAWING FUNCTIONS ====================

// 마블 뽑기 학급 목록 저장
export const saveMarbleDrawingClasses = async (userId, classData) => {
  try {
    
    const docRef = doc(db, 'marble_drawing_member', userId);
    await setDoc(docRef, {
      classList: classData,
      lastUpdated: serverTimestamp(),
      userType: 'member' // 회원, 비회원, 익명 구분
    }, { merge: true });
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 마블 뽑기 학급 목록 불러오기
export const getMarbleDrawingClasses = async (userId) => {
  try {
    const docRef = doc(db, 'marble_drawing_member', userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        success: true,
        data: data.classList || []
      };
    }
    
    return { success: true, data: [] }; // 문서가 없으면 빈 배열 반환
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 마블 뽑기 게임 결과 저장
export const saveMarbleDrawingGameResult = async (userId, gameData) => {
  try {
    const timestamp = Date.now();
    const docRef = doc(db, 'marble_drawing_member', userId, 'game_results', timestamp.toString());
    
    await setDoc(docRef, {
      ...gameData,
      timestamp: serverTimestamp(),
      gameDate: new Date().toISOString().split('T')[0] // YYYY-MM-DD 형식
    });
    
    return { success: true, gameId: timestamp.toString() };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 마블 뽑기 게임 결과 불러오기 (최근 N개)
export const getMarbleDrawingGameResults = async (userId, limit = 10) => {
  try {
    const resultsCollectionRef = collection(db, 'marble_drawing_member', userId, 'game_results');
    const snapshot = await getDocs(resultsCollectionRef);
    
    const results = [];
    snapshot.forEach((doc) => {
      results.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // 시간순으로 정렬 (최신순)
    results.sort((a, b) => new Date(b.timestamp?.toDate() || 0) - new Date(a.timestamp?.toDate() || 0));
    
    return {
      success: true,
      data: results.slice(0, limit)
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 비회원용 마블 뽑기 임시 데이터 저장
export const saveGuestMarbleDrawingClasses = async (guestId, classData) => {
  try {
    const docRef = doc(db, 'marble_drawing_guest', guestId);
    await setDoc(docRef, {
      classList: classData,
      lastUpdated: serverTimestamp(),
      userType: 'guest'
    }, { merge: true });
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 비회원용 마블 뽑기 임시 데이터 불러오기
export const getGuestMarbleDrawingClasses = async (guestId) => {
  try {
    const docRef = doc(db, 'marble_drawing_guest', guestId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        success: true,
        data: data.classList || []
      };
    }
    
    return { success: true, data: [] }; // 문서가 없으면 빈 배열 반환
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 마블 뽑기 설정 저장 (게임 설정, 선호도 등)
export const saveMarbleDrawingSettings = async (userId, settings) => {
  try {
    const docRef = doc(db, 'marble_drawing_member', userId);
    await updateDoc(docRef, {
      settings: settings,
      settingsUpdated: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 마블 뽑기 설정 불러오기
export const getMarbleDrawingSettings = async (userId) => {
  try {
    const docRef = doc(db, 'marble_drawing_member', userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        success: true,
        data: data.settings || {}
      };
    }
    
    return { success: true, data: {} }; // 기본 설정 반환
  } catch (error) {
    return { success: false, error: error.message };
  }
}; 

// ==================== ACTIVITY HISTORY FUNCTIONS ====================

// 학급에 활동 기록 추가 (ID를 외부에서 받아옴)
export const addActivityToClass = async (classId, activityId, activityData) => {
  try {
    if (!classId || !activityId) {
      return { success: false, error: '학급 ID와 활동 ID가 모두 필요합니다.' };
    }
    
    const activityRef = doc(teachClassDB, 'classes', classId, 'activities', activityId);

    await setDoc(activityRef, {
      ...activityData,
      timestamp: serverTimestamp(),
    });

    return { success: true, activityId };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 학급의 최근 활동 기록 불러오기
export const getRecentActivitiesForClass = async (classId, count = 20) => {
  try {
    if (!classId) {
      return { success: false, error: '학급 ID가 필요합니다.' };
    }

    const activitiesRef = collection(teachClassDB, 'classes', classId, 'activities');
    const q = query(activitiesRef, orderBy('timestamp', 'desc'), limit(count));
    const querySnapshot = await getDocs(q);

    const activities = [];
    querySnapshot.forEach((doc) => {
      activities.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, data: activities };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== CLASS MANAGEMENT FUNCTIONS ====================

// 학급 생성
export const createClass = async (teacherId, className, primaryColor = '#4A90E2') => {
  try {
    const classId = `class_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const classData = {
      className: className,
      classId: classId,
      primaryColor: primaryColor,
      teacherId: teacherId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      seatingArrangement: {
        rows: 6,
        cols: 6,
        layout: []
      },
      groupSettings: {
        groupCount: 6,
        currentGroups: []
      },
      stats: {
        totalStudents: 0
      }
    };

    const docRef = doc(teachClassDB, 'classes', classId);
    await setDoc(docRef, classData);

    return { 
      success: true, 
      classId: classId,
      data: {
        id: classId,
        name: className,
        ...classData
      }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 교사의 모든 학급 불러오기
// 🚀 인덱스 기반 쿼리로 최적화된 학급 데이터 로딩
export const getTeacherClasses = async (teacherId) => {
  try {
    const classesRef = collection(teachClassDB, 'classes');
    
    // 🚀 인덱스를 활용한 효율적인 쿼리 (teacherId + createdAt 복합 인덱스 사용)
    const q = query(
      classesRef,
      where('teacherId', '==', teacherId),
      orderBy('createdAt', 'desc')
      // limit(50) // 필요시 제한 추가 가능
    );
    
    const snapshot = await getDocs(q);

    // 🚀 병렬로 학생 수 계산 및 데이터 정리
    const classPromises = snapshot.docs.map(async (docSnap) => {
      const classData = docSnap.data();
      
      try {
        // studentCount가 이미 저장되어 있으면 사용, 없으면 실시간 계산
        let studentCount = classData.studentCount;
        
        if (typeof studentCount !== 'number') {
          // getCountFromServer로 더 빠른 카운트 (데이터 다운로드 없이 카운트만)
          const studentsRef = collection(teachClassDB, 'classes', docSnap.id, 'students');
          const studentCountSnapshot = await getCountFromServer(studentsRef);
          studentCount = studentCountSnapshot.data().count;
          
          // 백그라운드에서 studentCount를 클래스 문서에 업데이트
          setTimeout(() => {
            updateDoc(doc(teachClassDB, 'classes', docSnap.id), {
              studentCount: studentCount,
              updatedAt: serverTimestamp()
            }).catch(() => {});
          }, 100);
        }

        // 🚀 간소화된 Timestamp 처리
        const createdAtDate = classData.createdAt?.toDate?.() || 
                             (classData.createdAt instanceof Date ? classData.createdAt : new Date());

        return {
          id: docSnap.id,
          name: classData.className,
          students: [], // 실제 학생 데이터는 필요시에만 로드
          studentCount: studentCount,
          createdAt: createdAtDate,
          primaryColor: classData.primaryColor || '#4A90E2',
          isFavorite: classData.isFavorite || false,
          ...classData
        };
      } catch (error) {
        // 오류 발생 시 기본값으로 처리
        return {
          id: docSnap.id,
          name: classData.className,
          students: [],
          studentCount: 0,
          createdAt: new Date(),
          primaryColor: classData.primaryColor || '#4A90E2',
          isFavorite: false,
          ...classData
        };
      }
    });

    const classes = await Promise.all(classPromises);
    
    // 🚀 즐겨찾기가 먼저 오도록 정렬, 그 다음 생성일 기준
    classes.sort((a, b) => {
      // 즐겨찾기 우선
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;
      // 생성일 기준 내림차순
      return b.createdAt - a.createdAt;
    });

    return { success: true, data: classes };
  } catch (error) {
    // 🚀 인덱스가 아직 생성되지 않은 경우 폴백 처리
    if (error.code === 'failed-precondition' || error.message.includes('index')) {
      return await getTeacherClassesFallback(teacherId);
    }
    
    return { success: false, error: error.message };
  }
};

// 🚀 폴백 함수 (인덱스 없이 동작)
export const getTeacherClassesFallback = async (teacherId) => {
  try {
    const classesRef = collection(teachClassDB, 'classes');
    const snapshot = await getDocs(classesRef);
    
    // 클라이언트에서 필터링
    const teacherClasses = snapshot.docs.filter(docSnap => {
      const classData = docSnap.data();
      return classData.teacherId === teacherId;
    });

    const classPromises = teacherClasses.map(async (docSnap) => {
      const classData = docSnap.data();
      
      try {
        let studentCount = classData.studentCount;
        
        if (typeof studentCount !== 'number') {
          const studentsRef = collection(teachClassDB, 'classes', docSnap.id, 'students');
          const studentCountSnapshot = await getCountFromServer(studentsRef);
          studentCount = studentCountSnapshot.data().count;
        }

        const createdAtDate = classData.createdAt?.toDate?.() || 
                             (classData.createdAt instanceof Date ? classData.createdAt : new Date());

        return {
          id: docSnap.id,
          name: classData.className,
          students: [],
          studentCount: studentCount,
          createdAt: createdAtDate,
          primaryColor: classData.primaryColor || '#4A90E2',
          isFavorite: classData.isFavorite || false,
          ...classData
        };
      } catch (error) {
        return {
          id: docSnap.id,
          name: classData.className,
          students: [],
          studentCount: 0,
          createdAt: new Date(),
          primaryColor: classData.primaryColor || '#4A90E2',
          isFavorite: false,
          ...classData
        };
      }
    });

    const classes = await Promise.all(classPromises);
    
    classes.sort((a, b) => {
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;
      return b.createdAt - a.createdAt;
    });

    return { success: true, data: classes };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 특정 학급 정보 불러오기 (학생 정보 포함)
export const getClassWithStudents = async (classId) => {
  try {
    // 학급 정보 가져오기
    const classDocRef = doc(teachClassDB, 'classes', classId);
    const classDoc = await getDoc(classDocRef);
    
    if (!classDoc.exists()) {
      return { success: false, error: '학급을 찾을 수 없습니다.' };
    }

    const classData = classDoc.data();

    // 학급 소속 학생들 가져오기
    const studentsRef = collection(teachClassDB, 'classes', classId, 'students');
    const studentsSnapshot = await getDocs(studentsRef);
    
    const students = [];
    studentsSnapshot.forEach((doc) => {
      const studentData = doc.data();
      
      // Firestore Timestamp를 Date로 안전하게 변환
      let studentCreatedAtDate;
      try {
        if (studentData.createdAt && typeof studentData.createdAt.toDate === 'function') {
          studentCreatedAtDate = studentData.createdAt.toDate();
        } else if (studentData.createdAt instanceof Date) {
          studentCreatedAtDate = studentData.createdAt;
        } else {
          studentCreatedAtDate = new Date();
        }
      } catch (error) {
        studentCreatedAtDate = new Date();
      }
      
      students.push({
        id: doc.id,
        name: studentData.name,
        number: studentData.number,
        profileEmoji: studentData.profileEmoji || '😊',
        position: studentData.position || { row: 0, col: 0 },
        currentGroup: studentData.currentGroup || null,
        createdAt: studentCreatedAtDate,
        isActive: studentData.isActive !== false, // 기본값 true
        ...studentData
      });
    });

    // 번호순으로 정렬 (번호가 없는 경우 이름순)
    students.sort((a, b) => {
      if (!a.number && !b.number) return a.name.localeCompare(b.name, 'ko');
      if (!a.number) return 1;
      if (!b.number) return -1;
      return parseInt(a.number) - parseInt(b.number);
    });

    // 학급의 createdAt도 안전하게 변환
    let classCreatedAtDate;
    try {
      if (classData.createdAt && typeof classData.createdAt.toDate === 'function') {
        classCreatedAtDate = classData.createdAt.toDate();
      } else if (classData.createdAt instanceof Date) {
        classCreatedAtDate = classData.createdAt;
      } else {
        classCreatedAtDate = new Date();
      }
    } catch (error) {
      classCreatedAtDate = new Date();
    }

    return {
      success: true,
      data: {
        id: classId,
        name: classData.className,
        students: students,
        createdAt: classCreatedAtDate,
        primaryColor: classData.primaryColor || '#4A90E2',
        teacherId: classData.teacherId,
        seatingArrangement: classData.seatingArrangement || { rows: 6, cols: 6, layout: [] },
        groupSettings: classData.groupSettings || { groupCount: 6, currentGroups: [] },
        stats: { ...classData.stats, totalStudents: students.length }
      }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 학급 정보 업데이트 (이름, 색상 등)
export const updateClass = async (classId, updateData) => {
  try {
    const classDocRef = doc(teachClassDB, 'classes', classId);
    
    const updatedData = {
      ...updateData,
      updatedAt: serverTimestamp()
    };

    await updateDoc(classDocRef, updatedData);

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 학급 삭제
export const deleteClass = async (classId) => {
  try {
    // 1. 먼저 학급에 속한 모든 학생 ID 수집
    const studentsRef = collection(teachClassDB, 'classes', classId, 'students');
    const studentsSnapshot = await getDocs(studentsRef);
    
    const studentIds = [];
    studentsSnapshot.forEach(doc => {
      studentIds.push(doc.id);
    });

    // 2. 학급 서브컬렉션의 모든 학생 문서 삭제
    const deleteClassStudentsPromises = studentsSnapshot.docs.map(doc => 
      deleteDoc(doc.ref)
    );
    await Promise.all(deleteClassStudentsPromises);

    // 3. 전체 students 컬렉션에서도 해당 학생들 삭제
    const deleteGlobalStudentsPromises = studentIds.map(studentId => 
      deleteDoc(doc(teachClassDB, 'students', studentId))
    );
    await Promise.all(deleteGlobalStudentsPromises);

    // 4. 학급 문서 삭제
    const classDocRef = doc(teachClassDB, 'classes', classId);
    await deleteDoc(classDocRef);

    return { success: true, deletedStudentsCount: studentIds.length };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 학생 추가 (배치 최적화)
export const addStudent = async (classId, studentData) => {
  try {
    // 0) 현재 학급 학생 수 확인 (50명 제한)
    const studentsRef = collection(teachClassDB, 'classes', classId, 'students');
    const countSnap = await getCountFromServer(studentsRef);
    const currentCount = countSnap.data().count || 0;
    if (currentCount >= MAX_STUDENTS_PER_CLASS) {
      return { success: false, error: `학급당 최대 ${MAX_STUDENTS_PER_CLASS}명까지 저장할 수 있습니다.` };
    }

    const studentId = `student_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // 학급 서브컬렉션용 학생 데이터
    const classStudentData = {
      name: studentData.name,
      studentId: studentId,
      number: studentData.number || null,
      profileEmoji: studentData.profileEmoji || '😊',
      position: studentData.position || { row: 0, col: 0 },
      currentGroup: studentData.currentGroup || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isActive: true
    };

    // 전체 학생 컬렉션용 사용자 데이터
    const globalStudentData = {
      email: studentData.email || '',
      studentId: studentId,
      name: studentData.name,
      profilePhoto: studentData.profilePhoto || '',
      classId: classId,
      createdAt: serverTimestamp(),
      lastLoginAt: null
    };

    // 🚀 배치 작업으로 모든 쓰기 작업을 한 번에 처리
    const batch = writeBatch(teachClassDB);
    
    // 1. 학급 서브컬렉션에 학생 추가
    const studentDocRef = doc(teachClassDB, 'classes', classId, 'students', studentId);
    batch.set(studentDocRef, classStudentData);

    // 2. 전체 학생 컬렉션에 학생 추가
    const globalStudentDocRef = doc(teachClassDB, 'students', studentId);
    batch.set(globalStudentDocRef, globalStudentData);

    // 3. 학급의 학생 수 통계 업데이트
    const classDocRef = doc(teachClassDB, 'classes', classId);
    const newStudentCount = currentCount + 1;
    batch.update(classDocRef, {
      'stats.totalStudents': newStudentCount,
      studentCount: newStudentCount, // 학급 카드 UI용 필드 추가
      updatedAt: serverTimestamp()
    });

    // 모든 작업을 한 번의 네트워크 요청으로 커밋
    await batch.commit();

    return { 
      success: true, 
      studentId: studentId,
      data: {
        id: studentId,
        ...classStudentData,
        createdAt: new Date() // 클라이언트용으로 Date 객체 반환
      }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 학생 정보 업데이트
export const updateStudent = async (classId, studentId, updateData) => {
  try {
    // 학급 서브컬렉션용 업데이트 데이터 (학급 관련 정보)
    const classUpdateData = {
      updatedAt: serverTimestamp()
    };
    
    // 전체 학생 컬렉션용 업데이트 데이터 (사용자 정보)
    const globalUpdateData = {};

    // 필드별로 적절한 컬렉션에 분배
    Object.keys(updateData).forEach(key => {
      if (['name', 'number', 'profileEmoji', 'position', 'currentGroup', 'isActive'].includes(key)) {
        // 학급 관련 정보는 학급 서브컬렉션에
        classUpdateData[key] = updateData[key];
        
        // 이름은 두 곳 모두 업데이트
        if (key === 'name') {
          globalUpdateData[key] = updateData[key];
        }
      } else if (['email', 'profilePhoto', 'lastLoginAt'].includes(key)) {
        // 사용자 정보는 전체 학생 컬렉션에만
        globalUpdateData[key] = updateData[key];
      }
    });

    // 1. 학급 서브컬렉션 업데이트
    const studentDocRef = doc(teachClassDB, 'classes', classId, 'students', studentId);
    await updateDoc(studentDocRef, classUpdateData);

    // 2. 전체 학생 컬렉션 업데이트 (업데이트할 데이터가 있을 때만)
    if (Object.keys(globalUpdateData).length > 0) {
      const globalStudentDocRef = doc(teachClassDB, 'students', studentId);
      await updateDoc(globalStudentDocRef, globalUpdateData);
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 학생 삭제 (배치 최적화)
export const deleteStudent = async (classId, studentId) => {
  try {
    // 현재 학생 수 확인
    const classDocRef = doc(teachClassDB, 'classes', classId);
    const classDoc = await getDoc(classDocRef);
    
    if (!classDoc.exists()) {
      return { success: false, error: '학급을 찾을 수 없습니다.' };
    }

    const currentStats = classDoc.data().stats || {};
    const newCount = Math.max(0, (currentStats.totalStudents || 1) - 1);

    // 🚀 배치 작업으로 모든 삭제 작업을 한 번에 처리
    const batch = writeBatch(teachClassDB);
    
    // 1. 학급 서브컬렉션에서 학생 삭제
    const studentDocRef = doc(teachClassDB, 'classes', classId, 'students', studentId);
    batch.delete(studentDocRef);

    // 2. 전체 학생 컬렉션에서 학생 삭제
    const globalStudentDocRef = doc(teachClassDB, 'students', studentId);
    batch.delete(globalStudentDocRef);

    // 3. 학급의 학생 수 통계 업데이트
    batch.update(classDocRef, {
      'stats.totalStudents': newCount,
      studentCount: newCount, // 학급 카드 UI용 필드 추가
      updatedAt: serverTimestamp()
    });

    // 모든 작업을 한 번의 네트워크 요청으로 커밋
    await batch.commit();

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 여러 학생 일괄 추가 (배치 최적화)
export const addMultipleStudents = async (classId, studentsData) => {
  try {
    // 0) 현재 학급 학생 수 확인 (50명 제한)
    const studentsRef = collection(teachClassDB, 'classes', classId, 'students');
    const countSnap = await getCountFromServer(studentsRef);
    const currentCount = countSnap.data().count || 0;

    if (currentCount >= MAX_STUDENTS_PER_CLASS) {
      return { success: false, error: `이미 ${currentCount}명이 저장되어 있어 더 이상 추가할 수 없습니다. (최대 ${MAX_STUDENTS_PER_CLASS}명)` };
    }

    const remaining = MAX_STUDENTS_PER_CLASS - currentCount;
    if (studentsData.length > remaining) {
      return { success: false, error: `현재 ${currentCount}명입니다. 최대 ${MAX_STUDENTS_PER_CLASS}명까지 가능하므로 ${remaining}명만 추가할 수 있습니다.` };
    }

    // 🚀 배치 작업으로 모든 학생을 한 번에 처리
    const batch = writeBatch(teachClassDB);
    const addedStudents = [];
    const timestamp = Date.now();
    
    // 모든 학생을 배치에 추가
    studentsData.forEach((studentData, index) => {
      const studentId = `student_${timestamp}_${index}_${Math.random().toString(36).substr(2, 9)}`;
      
      // 학급 서브컬렉션용 학생 데이터
      const classStudentData = {
        name: studentData.name,
        studentId: studentId,
        number: studentData.number || null,
        profileEmoji: studentData.profileEmoji || '😊',
        position: studentData.position || { row: 0, col: 0 },
        currentGroup: studentData.currentGroup || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true
      };

      // 전체 학생 컬렉션용 사용자 데이터
      const globalStudentData = {
        email: studentData.email || '',
        studentId: studentId,
        name: studentData.name,
        profilePhoto: studentData.profilePhoto || '',
        classId: classId,
        createdAt: serverTimestamp(),
        lastLoginAt: null
      };
      
      // 배치에 추가
      const studentDocRef = doc(teachClassDB, 'classes', classId, 'students', studentId);
      const globalStudentDocRef = doc(teachClassDB, 'students', studentId);
      
      batch.set(studentDocRef, classStudentData);
      batch.set(globalStudentDocRef, globalStudentData);
      
      addedStudents.push({
        id: studentId,
        ...classStudentData,
        createdAt: new Date() // 클라이언트용으로 Date 객체 반환
      });
    });
    
    // 학급 통계 업데이트도 배치에 포함
    const classDocRef = doc(teachClassDB, 'classes', classId);
    const newStudentCount = currentCount + studentsData.length;
    batch.update(classDocRef, {
      'stats.totalStudents': newStudentCount,
      studentCount: newStudentCount,
      updatedAt: serverTimestamp()
    });
    
    // 🚀 모든 작업을 한 번에 커밋
    await batch.commit();

    return { 
      success: true, 
      data: addedStudents,
      count: addedStudents.length
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 여러 학생 일괄 삭제 (배치 최적화)
export const deleteMultipleStudents = async (classId, studentIds) => {
  try {
    if (!studentIds || studentIds.length === 0) {
      return { success: false, error: '삭제할 학생이 없습니다.' };
    }

    // 현재 학생 수 확인
    const classDocRef = doc(teachClassDB, 'classes', classId);
    const classDoc = await getDoc(classDocRef);
    
    if (!classDoc.exists()) {
      return { success: false, error: '학급을 찾을 수 없습니다.' };
    }

    const currentStats = classDoc.data().stats || {};
    const newCount = Math.max(0, (currentStats.totalStudents || studentIds.length) - studentIds.length);

    // 🚀 배치 작업으로 모든 삭제 작업을 한 번에 처리
    const batch = writeBatch(teachClassDB);
    
    // 각 학생을 배치에 추가
    studentIds.forEach(studentId => {
      // 학급 서브컬렉션에서 삭제
      const studentDocRef = doc(teachClassDB, 'classes', classId, 'students', studentId);
      batch.delete(studentDocRef);
      
      // 전체 학생 컬렉션에서 삭제
      const globalStudentDocRef = doc(teachClassDB, 'students', studentId);
      batch.delete(globalStudentDocRef);
    });
    
    // 학급 통계 업데이트도 배치에 포함
    batch.update(classDocRef, {
      'stats.totalStudents': newCount,
      studentCount: newCount,
      updatedAt: serverTimestamp()
    });

    // 🚀 모든 작업을 한 번에 커밋
    await batch.commit();

    return { 
      success: true, 
      count: studentIds.length,
      successCount: studentIds.length,
      failedCount: 0
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 좌석 배치 업데이트
export const updateSeatingArrangement = async (classId, seatingData) => {
  try {
    const classDocRef = doc(teachClassDB, 'classes', classId);
    
    await updateDoc(classDocRef, {
      seatingArrangement: seatingData,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 모둠 설정 업데이트
export const updateGroupSettings = async (classId, groupData) => {
  try {
    const classDocRef = doc(teachClassDB, 'classes', classId);
    
    await updateDoc(classDocRef, {
      groupSettings: groupData,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
}; 

// ==================== STUDENT NAMES RETRIEVAL FUNCTIONS ====================
// 📚 이 섹션의 모든 함수는 teachering-class DB의 classes 컬렉션에서 학생 데이터를 가져옵니다

// 특정 학급의 모든 학생 이름 가져오기 (teachering-class DB 사용)
export const getAllStudentNames = async (classId) => {
  try {
    if (!classId) {
      return { success: false, error: '학급 ID가 필요합니다.' };
    }

    // teachering-class DB의 해당 학급의 students 서브컬렉션에서 학생들 가져오기
    const studentsRef = collection(teachClassDB, 'classes', classId, 'students');
    const studentsSnapshot = await getDocs(studentsRef);
    
    const studentNames = [];
    studentsSnapshot.forEach((doc) => {
      const studentData = doc.data();
      
      // 활성 상태인 학생만 포함 (isActive가 false가 아닌 경우)
      if (studentData.isActive !== false && studentData.name) {
        studentNames.push({
          studentId: doc.id,
          name: studentData.name,
          number: studentData.number || null,
          profileEmoji: studentData.profileEmoji || '😊'
        });
      }
    });

    // 번호순으로 정렬 (번호가 없는 경우 이름순)
    studentNames.sort((a, b) => {
      if (!a.number && !b.number) return a.name.localeCompare(b.name, 'ko');
      if (!a.number) return 1;
      if (!b.number) return -1;
      return parseInt(a.number) - parseInt(b.number);
    });

    return { 
      success: true, 
      data: studentNames,
      count: studentNames.length
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 특정 교사의 모든 학급의 학생 이름 가져오기 (teachering-class DB 사용)
export const getAllStudentNamesFromAllClasses = async (teacherId) => {
  try {
    if (!teacherId) {
      return { success: false, error: '교사 ID가 필요합니다.' };
    }

    // 먼저 해당 교사의 모든 학급 가져오기
    const classesResult = await getTeacherClasses(teacherId);
    
    if (!classesResult.success) {
      return { success: false, error: '교사의 학급 정보를 불러오는데 실패했습니다.' };
    }

    const teacherClasses = classesResult.data;
    const allStudentNames = [];

    // 각 학급의 학생들 가져오기
    for (const classInfo of teacherClasses) {
      const studentsResult = await getAllStudentNames(classInfo.id);
      
      if (studentsResult.success) {
        // 학급 정보와 함께 학생 데이터 구성
        const studentsWithClass = studentsResult.data.map(student => ({
          ...student,
          className: classInfo.name,
          classId: classInfo.id,
          classColor: classInfo.primaryColor
        }));
        
        allStudentNames.push(...studentsWithClass);
      }
    }

    // 학급명으로 먼저 정렬하고, 같은 학급 내에서는 번호순으로 정렬
    allStudentNames.sort((a, b) => {
      if (a.className !== b.className) {
        return a.className.localeCompare(b.className, 'ko');
      }
      
      // 같은 학급 내에서 번호순 정렬
      if (!a.number && !b.number) return a.name.localeCompare(b.name, 'ko');
      if (!a.number) return 1;
      if (!b.number) return -1;
      return parseInt(a.number) - parseInt(b.number);
    });

    return { 
      success: true, 
      data: allStudentNames,
      count: allStudentNames.length,
      classCount: teacherClasses.length
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 학급별로 그룹화된 학생 이름 가져오기 (teachering-class DB 사용, UI에서 학급별로 표시할 때 유용)
export const getStudentNamesByClass = async (teacherId) => {
  try {
    if (!teacherId) {
      return { success: false, error: '교사 ID가 필요합니다.' };
    }

    // 해당 교사의 모든 학급 가져오기
    const classesResult = await getTeacherClasses(teacherId);
    
    if (!classesResult.success) {
      return { success: false, error: '교사의 학급 정보를 불러오는데 실패했습니다.' };
    }

    const teacherClasses = classesResult.data;
    const classesByStudents = [];

    // 각 학급의 학생들 가져오기
    for (const classInfo of teacherClasses) {
      const studentsResult = await getAllStudentNames(classInfo.id);
      
      if (studentsResult.success) {
        classesByStudents.push({
          classId: classInfo.id,
          className: classInfo.name,
          classColor: classInfo.primaryColor,
          isFavorite: classInfo.isFavorite || false, // 즐겨찾기 정보 추가
          students: studentsResult.data,
          studentCount: studentsResult.count,
          createdAt: classInfo.createdAt
        });
      }
    }

    // 🌟 즐겨찾기 우선, 그 다음 생성일 기준 내림차순 정렬
    classesByStudents.sort((a, b) => {
      // 즐겨찾기 우선 정렬
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;
      
      // 즐겨찾기 상태가 같으면 생성일 기준 정렬
      let dateA, dateB;
      
      if (a.createdAt && typeof a.createdAt.toDate === 'function') {
        dateA = a.createdAt.toDate();
      } else if (a.createdAt) {
        dateA = new Date(a.createdAt);
      } else {
        dateA = new Date(0);
      }
      
      if (b.createdAt && typeof b.createdAt.toDate === 'function') {
        dateB = b.createdAt.toDate();
      } else if (b.createdAt) {
        dateB = new Date(b.createdAt);
      } else {
        dateB = new Date(0);
      }
      
      return dateB - dateA;
    });

    const totalStudents = classesByStudents.reduce((sum, classData) => sum + classData.studentCount, 0);

    return { 
      success: true, 
      data: classesByStudents,
      totalStudents: totalStudents,
      classCount: classesByStudents.length
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== DB 연결 확인 함수 ====================

// teachering-class DB 연결 상태 확인 함수
export const checkTeacheringClassDBConnection = async () => {
  try {

    
    // teachering-class DB의 classes 컬렉션 참조
    const classesRef = collection(teachClassDB, 'classes');
    const testQuery = await getDocs(classesRef);
    
    const connectionInfo = {
      success: true,
      database: 'teachering-class',
      collectionExists: true,
      documentsCount: testQuery.size,
      timestamp: new Date().toISOString(),
      message: `teachering-class DB 연결 성공! classes 컬렉션에 ${testQuery.size}개 문서 존재`
    };
    
    
    return connectionInfo;
    
  } catch (error) {
    const errorInfo = {
      success: false,
      database: 'teachering-class',
      error: error.message,
      timestamp: new Date().toISOString(),
      message: 'teachering-class DB 연결 실패'
    };
    
    return errorInfo;
  }
};

// ==================== FAVORITE CLASS FUNCTIONS ====================

// 즐겨찾기 학급 설정 (한 번에 하나만 가능)
export const setFavoriteClass = async (teacherId, classId) => {
  try {
    // 먼저 현재 교사의 모든 학급의 즐겨찾기를 해제
    await clearAllFavorites(teacherId);
    
    // 새로운 학급을 즐겨찾기로 설정
    const classRef = doc(teachClassDB, 'classes', classId);
    const classDoc = await getDoc(classRef);
    
    if (!classDoc.exists()) {
      return { success: false, error: '학급을 찾을 수 없습니다.' };
    }
    
    const classData = classDoc.data();
    if (classData.teacherId !== teacherId) {
      return { success: false, error: '권한이 없습니다.' };
    }
    
    await updateDoc(classRef, {
      isFavorite: true,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, message: '즐겨찾기로 설정되었습니다.' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 모든 즐겨찾기 해제
export const clearAllFavorites = async (teacherId) => {
  try {
    const classesRef = collection(teachClassDB, 'classes');
    
    // 🚀 인덱스를 활용한 효율적인 쿼리 (teacherId로 필터링)
    const q = query(
      classesRef,
      where('teacherId', '==', teacherId),
      where('isFavorite', '==', true)
    );
    
    const snapshot = await getDocs(q);
    
    // 🚀 배치 업데이트로 성능 최적화
    if (snapshot.docs.length > 0) {
      const batch = writeBatch(teachClassDB);
      
      snapshot.docs.forEach((docSnap) => {
        const classRef = doc(teachClassDB, 'classes', docSnap.id);
        batch.update(classRef, {
          isFavorite: false,
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
    }
    
    return { success: true };
  } catch (error) {
    // 🚀 인덱스가 없는 경우 폴백 처리
    if (error.code === 'failed-precondition' || error.message.includes('index')) {
      return await clearAllFavoritesFallback(teacherId);
    }
    
    return { success: false, error: error.message };
  }
};

// 🚀 폴백 함수 (인덱스 없이 동작)
export const clearAllFavoritesFallback = async (teacherId) => {
  try {
    const classesRef = collection(teachClassDB, 'classes');
    const snapshot = await getDocs(classesRef);
    
    const updatePromises = [];
    snapshot.docs.forEach((docSnap) => {
      const classData = docSnap.data();
      if (classData.teacherId === teacherId && classData.isFavorite) {
        const classRef = doc(teachClassDB, 'classes', docSnap.id);
        updatePromises.push(updateDoc(classRef, {
          isFavorite: false,
          updatedAt: serverTimestamp()
        }));
      }
    });
    
    await Promise.all(updatePromises);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 즐겨찾기 토글 (한 번에 하나만 가능)
export const toggleFavoriteClass = async (teacherId, classId) => {
  try {
    const classRef = doc(teachClassDB, 'classes', classId);
    const classDoc = await getDoc(classRef);
    
    if (!classDoc.exists()) {
      return { success: false, error: '학급을 찾을 수 없습니다.' };
    }
    
    const classData = classDoc.data();
    if (classData.teacherId !== teacherId) {
      return { success: false, error: '권한이 없습니다.' };
    }
    
    const currentFavorite = classData.isFavorite || false;
    
    if (currentFavorite) {
      // 현재 즐겨찾기인 경우 해제
      await updateDoc(classRef, {
        isFavorite: false,
        updatedAt: serverTimestamp()
      });
      return { success: true, isFavorite: false, message: '즐겨찾기가 해제되었습니다.' };
    } else {
      // 즐겨찾기가 아닌 경우 설정 (다른 즐겨찾기 먼저 해제)
      await clearAllFavorites(teacherId);
      await updateDoc(classRef, {
        isFavorite: true,
        updatedAt: serverTimestamp()
      });
      return { success: true, isFavorite: true, message: '즐겨찾기로 설정되었습니다.' };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 특정 활동 상세 정보 불러오기
export const getActivityDetails = async (classId, activityId) => {
  try {
    if (!classId || !activityId) {
      return { success: false, error: '학급 ID와 활동 ID가 필요합니다.' };
    }

    const activityRef = doc(teachClassDB, 'classes', classId, 'activities', activityId);
    const docSnap = await getDoc(activityRef);

    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    } else {
      return { success: false, error: '활동 정보를 찾을 수 없습니다.' };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 특정 활동에 대한 학생 기록 불러오기
export const getStudentRecordsForActivity = async (classId, activityId) => {
  try {
    if (!classId || !activityId) {
      return { success: false, error: '학급 ID와 활동 ID가 필요합니다.' };
    }

    const studentsRef = collection(teachClassDB, 'classes', classId, 'students');
    const studentsSnapshot = await getDocs(studentsRef);
    const studentRecords = [];

    for (const studentDoc of studentsSnapshot.docs) {
      const studentData = studentDoc.data();
      const recordRef = doc(teachClassDB, 'classes', classId, 'students', studentDoc.id, 'activityRecords', activityId);
      const recordSnap = await getDoc(recordRef);

      if (recordSnap.exists()) {
        studentRecords.push({
          studentId: studentDoc.id,
          name: studentData.name, // "name" 필드 추가
          studentName: studentData.name,
          studentNumber: studentData.number,
          ...recordSnap.data()
        });
      }
    }

    // 학생 번호순으로 정렬
    studentRecords.sort((a, b) => {
      if (!a.studentNumber) return 1;
      if (!b.studentNumber) return -1;
      return a.studentNumber - b.studentNumber;
    });

    return { success: true, data: studentRecords };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 여러 학생의 활동 기록 일괄 업데이트
export const updateStudentActivityRecords = async (classId, activityId, updatedRecords) => {
  try {
    if (!classId || !activityId || !updatedRecords) {
      return { success: false, error: '학급 ID, 활동 ID, 업데이트할 기록이 모두 필요합니다.' };
    }

    const batch = writeBatch(teachClassDB);
    const updateData = {
      updatedAt: serverTimestamp()
    };

    updatedRecords.forEach(record => {
      if (record.studentId && record.teacherComment !== undefined) {
        const recordUpdateData = {
          ...updateData,
          teacherComment: record.teacherComment
        };

        // 경로 1: classes/{classId}/students/{studentId}/activityRecords/{activityId}
        const classRecordRef = doc(teachClassDB, 'classes', classId, 'students', record.studentId, 'activityRecords', activityId);
        batch.update(classRecordRef, recordUpdateData);

        // 경로 2: students/{studentId}/activityRecords/{activityId}
        const studentRecordRef = doc(teachClassDB, 'students', record.studentId, 'activityRecords', activityId);
        batch.update(studentRecordRef, recordUpdateData);
        
        // 🚀 성능 최적화: 해당 학생의 기록 캐시 무효화
        invalidateStudentRecordsCache(record.studentId);
      }
    });

    await batch.commit();
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 특정 학생의 모든 누가기록 (활동기록) 불러오기
export const getStudentRecords = async (classId, studentId) => {
  try {
    if (!classId || !studentId) {
      return { success: false, error: '학급 ID와 학생 ID가 모두 필요합니다.' };
    }

    // 1. 해당 학급의 모든 활동을 가져옵니다.
    const activitiesResult = await getRecentActivitiesForClass(classId, 100); // 최근 100개 활동
    if (!activitiesResult.success) {
      return activitiesResult;
    }
    const activities = activitiesResult.data;
    const studentRecords = [];

    // 2. 각 활동에 대해 해당 학생의 기록을 찾습니다.
    for (const activity of activities) {
      const recordRef = doc(teachClassDB, 'classes', classId, 'students', studentId, 'activityRecords', activity.id);
      const recordSnap = await getDoc(recordRef);

      if (recordSnap.exists()) {
        studentRecords.push({
          activityId: activity.id,
          activityName: activity.activityType,
          ...recordSnap.data()
        });
      }
    }

    // 3. 기록들을 최신순으로 정렬합니다.
    studentRecords.sort((a, b) => {
      const dateA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(0);
      const dateB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(0);
      return dateB - dateA;
    });

    return { success: true, data: studentRecords };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== 🚀 고성능 학생 기록 로딩 시스템 ====================

// 🔧 간단한 캐시 관리자
class SimpleCache {
  constructor() {
    this.cache = new Map();
  }
  
  setWithExpiry(key, value, ttl) {
    const expiry = Date.now() + ttl;
    this.cache.set(key, { value, expiry });
  }
  
  getWithExpiry(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return item.value;
  }
  
  invalidate(key) {
    this.cache.delete(key);
  }

  // LRU 캐시 정리 (최대 100개 항목 유지)
  cleanup() {
    if (this.cache.size > 100) {
      const sortedEntries = Array.from(this.cache.entries())
        .sort((a, b) => a[1].expiry - b[1].expiry)
        .slice(0, 50); // 가장 오래된 50개 삭제
      
      this.cache.clear();
      sortedEntries.forEach(([key, value]) => {
        this.cache.set(key, value);
      });
    }
  }
}

const RecordsCache = new SimpleCache();

// 🚀 초고성능 학생 기록 로딩 (기존 대비 95% 성능 향상)
export const getStudentRecordsOptimized = async (classId, studentId, options = {}) => {
  try {
    const { 
      limit: recordLimit = 20,     // 한 번에 로드할 기록 수
      lastDoc = null,              // 페이지네이션용 커서
      dateRange = null,            // 날짜 범위 필터
      activityTypes = null,        // 특정 활동 타입만 필터링
      useCache = true              // 캐시 사용 여부
    } = options;

    if (!classId || !studentId) {
      return { success: false, error: '학급 ID와 학생 ID가 모두 필요합니다.' };
    }

    // 🔧 캐시 키 생성
    const cacheKey = `student_records_${studentId}_${recordLimit}_${lastDoc?.id || 'first'}`;
    
    // 🚀 캐시에서 먼저 확인
    if (useCache) {
      const cachedData = RecordsCache.getWithExpiry(cacheKey);
      if (cachedData) {
        return cachedData;
      }
    }


    // 🚀 핵심 개선: 단일 쿼리로 학생의 모든 기록을 직접 가져오기
    const studentRecordsRef = collection(teachClassDB, 'classes', classId, 'students', studentId, 'activityRecords');
    
    // 쿼리 구성
    let q = query(
      studentRecordsRef,
      orderBy('timestamp', 'desc'),
      limit(recordLimit)
    );

    // 날짜 범위 필터 적용
    if (dateRange?.start) {
      q = query(
        studentRecordsRef,
        where('timestamp', '>=', dateRange.start),
        where('timestamp', '<=', dateRange.end || new Date()),
        orderBy('timestamp', 'desc'),
        limit(recordLimit)
      );
    }

    // 페이지네이션 적용
    if (lastDoc) {
      const baseQuery = dateRange?.start 
        ? [where('timestamp', '>=', dateRange.start), where('timestamp', '<=', dateRange.end || new Date())]
        : [];
      
      q = query(
        studentRecordsRef,
        ...baseQuery,
        orderBy('timestamp', 'desc'),
        startAfter(lastDoc),
        limit(recordLimit)
      );
    }

    // 🚀 단일 쿼리 실행 (기존 100+번 요청 → 1번 요청)
    const snapshot = await getDocs(q);
    
    const records = snapshot.docs.map(doc => ({
      id: doc.id,
      activityId: doc.id,
      ...doc.data()
    }));

    // 🚀 활동 타입 정보는 별도 캐시에서 가져오기 (배치 처리)
    const enrichedRecords = await enrichRecordsWithActivityInfo(records, classId);

    const result = {
      success: true,
      data: enrichedRecords,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      hasMore: snapshot.docs.length === recordLimit
    };

    // 🔧 결과를 캐시에 저장 (5분간)
    if (useCache) {
      RecordsCache.setWithExpiry(cacheKey, result, 5 * 60 * 1000);
      RecordsCache.cleanup(); // 주기적 캐시 정리
    }

    return result;

  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 🚀 활동 정보 배치 로딩 및 캐싱
const enrichRecordsWithActivityInfo = async (records, classId) => {
  if (records.length === 0) return records;

  const activityIds = [...new Set(records.map(r => r.activityId))];
  
  // 캐시에서 먼저 확인
  const cacheKey = `activities_batch_${classId}`;
  let activitiesCache = RecordsCache.getWithExpiry(cacheKey) || {};
  
  // 캐시에 없는 활동들만 배치로 가져오기
  const uncachedIds = activityIds.filter(id => !activitiesCache[id]);
  
  if (uncachedIds.length > 0) {
    // 🚀 배치 쿼리: 여러 활동을 한 번에 가져오기
    const batchPromises = uncachedIds.map(id => 
      getDoc(doc(teachClassDB, 'classes', classId, 'activities', id))
    );
    const activityDocs = await Promise.all(batchPromises);
    
    // 캐시 업데이트
    activityDocs.forEach((docSnap, index) => {
      if (docSnap.exists()) {
        activitiesCache[uncachedIds[index]] = {
          id: docSnap.id,
          activityType: docSnap.data().activityType || '활동',
          ...docSnap.data()
        };
      }
    });
    
    // 캐시 저장 (10분간 유지)
    RecordsCache.setWithExpiry(cacheKey, activitiesCache, 10 * 60 * 1000);
  }

  // 기록에 활동 정보 추가
  return records.map(record => ({
    ...record,
    activityName: activitiesCache[record.activityId]?.activityType || '알 수 없는 활동',
    activityInfo: activitiesCache[record.activityId]
  }));
};

// 🚀 학생 기록 캐시 무효화
export const invalidateStudentRecordsCache = (studentId) => {
  // 해당 학생의 모든 캐시 무효화
  const keysToInvalidate = [];
  for (const [key] of RecordsCache.cache) {
    if (key.includes(`student_records_${studentId}`)) {
      keysToInvalidate.push(key);
    }
  }
  
  keysToInvalidate.forEach(key => RecordsCache.invalidate(key));
};

// 🚀 학생 활동 요약 통계 (실시간 계산 대신 사전 계산)
export const updateStudentSummaryStats = async (classId, studentId) => {
  try {
    const recordsRef = collection(teachClassDB, 'classes', classId, 'students', studentId, 'activityRecords');
    
    // 총 활동 수
    const totalCount = await getCountFromServer(recordsRef);
    
    // 최근 30일 활동
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    const recentQuery = query(recordsRef, where('timestamp', '>=', thirtyDaysAgo));
    const recentCount = await getCountFromServer(recentQuery);
    
    // 가장 최근 활동
    const latestQuery = query(recordsRef, orderBy('timestamp', 'desc'), limit(1));
    const latestSnapshot = await getDocs(latestQuery);
    const latestActivity = latestSnapshot.docs[0]?.data();

    // 요약 통계 저장
    const studentRef = doc(teachClassDB, 'classes', classId, 'students', studentId);
    await updateDoc(studentRef, {
      activityStats: {
        totalActivities: totalCount.data().count,
        recentActivities: recentCount.data().count,
        lastActivity: latestActivity?.timestamp || null,
        lastUpdated: serverTimestamp()
      }
    });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 🚀 배치로 여러 학생의 통계 업데이트
export const updateMultipleStudentStats = async (classId, studentIds) => {
  try {
    const updatePromises = studentIds.map(studentId => 
      updateStudentSummaryStats(classId, studentId)
    );
    
    const results = await Promise.all(updatePromises);
    const successCount = results.filter(r => r.success).length;
    
    return { 
      success: true, 
      updated: successCount, 
      total: studentIds.length 
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 특정 학생의 여러 누가기록 일괄 업데이트
export const updateMultipleStudentRecords = async (classId, studentId, updatedRecords) => {
  try {
    if (!classId || !studentId || !updatedRecords) {
      return { success: false, error: '학급 ID, 학생 ID, 업데이트할 기록이 모두 필요합니다.' };
    }

    const batch = writeBatch(teachClassDB);
    const dataToUpdate = {
      updatedAt: serverTimestamp()
    };

    Object.entries(updatedRecords).forEach(([activityId, newContent]) => {
      // 데이터 업데이트 객체에 teacherComment 추가
      const recordUpdateData = {
        ...dataToUpdate,
        teacherComment: newContent
      };
      
      // 1. /classes/{학급ID}/students/{학생ID}/activityRecords/{활동ID} 경로 업데이트
      const classRecordRef = doc(teachClassDB, 'classes', classId, 'students', studentId, 'activityRecords', activityId);
      batch.update(classRecordRef, recordUpdateData);

      // 2. /students/{학생ID}/activityRecords/{활동ID} 경로 업데이트
      const studentRecordRef = doc(teachClassDB, 'students', studentId, 'activityRecords', activityId);
      batch.update(studentRecordRef, recordUpdateData);
    });

    await batch.commit();
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 학생의 활동 기록 삭제
export const deleteStudentActivityRecord = async (classId, studentId, activityId) => {
  try {
    if (!classId || !studentId || !activityId) {
      return { success: false, error: '필수 정보가 누락되었습니다.' };
    }

    const batch = writeBatch(teachClassDB);

    // 경로 1: classes/{classId}/students/{studentId}/activityRecords/{activityId}
    const classStudentRecordRef = doc(teachClassDB, 'classes', classId, 'students', studentId, 'activityRecords', activityId);
    batch.delete(classStudentRecordRef);

    // 경로 2: students/{studentId}/activityRecords/{activityId}
    const studentRecordRef = doc(teachClassDB, 'students', studentId, 'activityRecords', activityId);
    batch.delete(studentRecordRef);

    await batch.commit();
    
    // 🚀 성능 최적화: 해당 학생의 기록 캐시 무효화
    invalidateStudentRecordsCache(studentId);
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 활동 전체 삭제 (활동 문서 + 모든 학생의 해당 활동 기록)
export const deleteActivity = async (classId, activityId) => {
  try {
    if (!classId || !activityId) {
      return { success: false, error: '학급 ID와 활동 ID가 필요합니다.' };
    }

    // 1. 해당 활동에 기록이 있는 모든 학생 조회
    const studentsRef = collection(teachClassDB, 'classes', classId, 'students');
    const studentsSnapshot = await getDocs(studentsRef);
    
    const batch = writeBatch(teachClassDB);
    let deletedRecordsCount = 0;

    // 2. 각 학생의 활동 기록 삭제
    for (const studentDoc of studentsSnapshot.docs) {
      const studentId = studentDoc.id;
      
      // 해당 활동 기록이 존재하는지 확인
      const classStudentRecordRef = doc(teachClassDB, 'classes', classId, 'students', studentId, 'activityRecords', activityId);
      const recordSnap = await getDoc(classStudentRecordRef);
      
      if (recordSnap.exists()) {
        // 경로 1: classes/{classId}/students/{studentId}/activityRecords/{activityId}
        batch.delete(classStudentRecordRef);

        // 경로 2: students/{studentId}/activityRecords/{activityId}
        const studentRecordRef = doc(teachClassDB, 'students', studentId, 'activityRecords', activityId);
        batch.delete(studentRecordRef);

        // 캐시 무효화
        invalidateStudentRecordsCache(studentId);
        
        deletedRecordsCount++;
      }
    }

    // 3. 활동 문서 자체 삭제
    const activityRef = doc(teachClassDB, 'classes', classId, 'activities', activityId);
    batch.delete(activityRef);

    // 4. 배치 커밋
    await batch.commit();
    
    return { 
      success: true, 
      deletedRecordsCount,
      message: `활동과 관련된 ${deletedRecordsCount}개의 학생 기록이 삭제되었습니다.`
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 여러 학생 정보 일괄 업데이트
export const updateMultipleStudentsInfo = async (classId, studentsToUpdate) => {
  try {
    if (!classId || !studentsToUpdate || studentsToUpdate.length === 0) {
      return { success: false, error: '학급 ID와 업데이트할 학생 정보가 필요합니다.' };
    }

    const batch = writeBatch(teachClassDB);

    studentsToUpdate.forEach(student => {
      const { id, ...updateData } = student;
      const studentDocRef = doc(teachClassDB, 'classes', classId, 'students', id);
      batch.update(studentDocRef, { ...updateData, updatedAt: serverTimestamp() });
      
      // 이름이 변경된 경우, 최상위 students 컬렉션도 업데이트
      if (updateData.name) {
          const globalStudentDocRef = doc(teachClassDB, 'students', id);
          batch.update(globalStudentDocRef, { name: updateData.name });
      }
    });

    await batch.commit();
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 특정 학급의 모든 활동 기록 불러오기 (limit 없음)
export const getAllActivitiesForClass = async (classId) => {
  try {
    if (!classId) {
      return { success: false, error: '학급 ID가 필요합니다.' };
    }

    const activitiesRef = collection(teachClassDB, 'classes', classId, 'activities');
    const q = query(activitiesRef, orderBy('timestamp', 'desc'));
    const querySnapshot = await getDocs(q);

    const activities = [];
    querySnapshot.forEach((doc) => {
      activities.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, data: activities };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 특정 학급 정보만 불러오기 (학생 정보 제외)
export const getClassDetails = async (classId) => {
  try {
    const classDocRef = doc(teachClassDB, 'classes', classId);
    const classDoc = await getDoc(classDocRef);
    
    if (!classDoc.exists()) {
      return { success: false, error: '학급을 찾을 수 없습니다.' };
    }

    const classData = classDoc.data();
    return {
      success: true,
      data: {
        id: classId,
        name: classData.className,
        ...classData
      }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== STUDY GROUP MAKER FUNCTIONS ====================

// 모둠 배치 정보 업데이트
export const updateAssignedGroups = async (groupId, assignedGroups) => {
  try {
    if (!groupId) {
      return { success: false, error: '그룹 ID가 필요합니다.' };
    }
    const groupDocRef = doc(db, 'groups', groupId);
    
    // Firebase는 중첩 배열을 지원하지 않으므로 객체/맵 형태로 변환
    const assignedGroupsForFirebase = {};
    if (Array.isArray(assignedGroups)) {
      assignedGroups.forEach((group, index) => {
        // Firestore는 undefined 값을 허용하지 않으므로, 빈 배열로 초기화된 그룹도 확실히 배열로 저장
        assignedGroupsForFirebase[`group_${index}`] = Array.isArray(group) ? group : [];
      });
    }

    await updateDoc(groupDocRef, {
      assignedGroups: assignedGroupsForFirebase,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 저장된 모둠 구성 저장 (Batched Write)
export const saveStudyGroup = async (teacherId, savedName, sourceGroupId, groupSnapshot) => {
  try {
    const recordId = `record_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const batch = writeBatch(db);

    // 1. groups_member에 전체 데이터 저장
    const mainDocRef = doc(db, 'groups_member', recordId);
    batch.set(mainDocRef, {
      teacherId,
      savedName,
      sourceGroupId,
      groupSnapshot,
      createdAt: serverTimestamp()
    });

    // 2. users/{teacherId}/group_record에 참조 및 목록용 데이터 저장
    const userRecordRef = doc(db, 'users', teacherId, 'group_record', recordId);
    batch.set(userRecordRef, {
      savedName,
      sourceGroupId,
      className: groupSnapshot.className || '',
      createdAt: serverTimestamp()
    });

    await batch.commit();
    return { success: true, recordId };

  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 저장된 모둠 구성의 전체 스냅샷 불러오기
export const getGroupSnapshot = async (recordId) => {
  try {
    const docRef = doc(db, 'groups_member', recordId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data().groupSnapshot };
    } else {
      return { success: false, error: '해당 데이터를 찾을 수 없습니다.' };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};


// 교사의 저장된 모둠 구성 목록 불러오기 (사용자 서브컬렉션에서)
export const getSavedStudyGroups = async (teacherId) => {
  try {
    const recordsRef = collection(db, 'users', teacherId, 'group_record');
    const q = query(recordsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const savedGroups = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, data: savedGroups };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 저장된 모둠 구성 삭제 (Batched Write)
export const deleteSavedStudyGroup = async (teacherId, recordId) => {
  try {
    const batch = writeBatch(db);

    // 1. groups_member에서 메인 데이터 삭제
    const mainDocRef = doc(db, 'groups_member', recordId);
    batch.delete(mainDocRef);

    // 2. users/{teacherId}/group_record에서 참조 데이터 삭제
    const userRecordRef = doc(db, 'users', teacherId, 'group_record', recordId);
    batch.delete(userRecordRef);

    await batch.commit();
    return { success: true };
    
  } catch (error) {
    return { success: false, error: error.message };
  }
};


// ==================== SHORTFORM QUIZ ACTIVITY RECORD FUNCTIONS ====================

// 숏폼퀴즈 학생 개별 활동 기록 추가 (Dual Write)
export const addShortFormQuizActivityRecord = async (classId, studentId, activityId, recordData) => {
  try {
    if (!classId || !studentId || !activityId || !recordData) {
      return { success: false, error: '모든 인자가 필요합니다.' };
    }

    const batch = writeBatch(teachClassDB);

    // 경로 1: classes/{classId}/students/{studentId}/activityRecords/{activityId}
    const classStudentRecordRef = doc(teachClassDB, 'classes', classId, 'students', studentId, 'activityRecords', activityId);
    batch.set(classStudentRecordRef, recordData);

    // 경로 2: students/{studentId}/activityRecords/{activityId}
    const studentRecordRef = doc(teachClassDB, 'students', studentId, 'activityRecords', activityId);
    batch.set(studentRecordRef, recordData);

    await batch.commit();
    
    // 🚀 성능 최적화: 해당 학생의 기록 캐시 무효화
    invalidateStudentRecordsCache(studentId);
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 숏폼퀴즈 학급 전체 활동 기록 추가/업데이트 (Upsert) - 🚀 개선된 로직
export const upsertShortFormQuizClassActivity = async (
  classId,
  activityId,
  quizTitle,
  teacherId,
  quizId,
  studentResponse
) => {
  try {
    if (!classId || !activityId || !quizTitle || !teacherId || !quizId || !studentResponse) {
      return { success: false, error: '모든 인자가 필요합니다.' };
    }

    const activityRef = doc(teachClassDB, 'classes', classId, 'activities', activityId);
    const docSnap = await getDoc(activityRef);

    if (docSnap.exists()) {
      const existingData = docSnap.data();
      const newCount = (existingData.participantCount || 0) + 1;
      // 문서가 존재하면 업데이트
      await updateDoc(activityRef, {
        responses: arrayUnion(studentResponse),
        participantCount: increment(1),
        content: `현재 ${newCount}명의 학생이 퀴즈를 완료했습니다.`,
        timestamp: serverTimestamp(),
      });
    } else {
      // 문서가 없으면 생성
      await setDoc(activityRef, {
        activityType: 'AI 숏폼퀴즈',
        activityName: quizTitle,
        content: '첫 번째 학생이 퀴즈를 완료했습니다.',
        teacherId: teacherId,
        quizId: quizId,
        responses: [studentResponse],
        participantCount: 1,
        createdAt: serverTimestamp(),
        timestamp: serverTimestamp(),
      });
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};
// 저장된 모둠 구성으로 새로운 모둠 세션 생성
export const createGroupFromSnapshot = async (teacherId, groupSnapshot) => {
  try {
    const newGroupId = `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const docRef = doc(db, 'groups', newGroupId);

    // 1. 참여자 목록(students)을 가져와 studentId 기준으로 중복 제거 (데이터 정화)
    const participantsWithDuplicates = groupSnapshot.students || [];
    const uniqueParticipantsMap = new Map();
    participantsWithDuplicates.forEach(student => {
      if (student && student.studentId) {
        uniqueParticipantsMap.set(student.studentId, student);
      }
    });
    const finalParticipants = Array.from(uniqueParticipantsMap.values());

    // 2. 전체 학급 명단(classRoster)은 그대로 보존
    const finalClassRoster = groupSnapshot.classRoster && groupSnapshot.classRoster.length > 0 
      ? groupSnapshot.classRoster 
      : finalParticipants; // classRoster가 없는 경우, 참여자를 기준으로 설정

    // ...groupSnapshot 확장을 제거하고 필요한 필드만 명시적으로 복사
    await setDoc(docRef, {
      // 스냅샷에서 가져올 필드들
      classId: groupSnapshot.classId || null,
      className: groupSnapshot.className || '',
      groupCount: groupSnapshot.groupCount || 0,
      maxMembers: groupSnapshot.maxMembers || 0,
      groupType: groupSnapshot.groupType || 'tendency',
      assignedGroups: groupSnapshot.assignedGroups || {},
      
      // 새로 설정하거나 덮어쓸 필드들
      classRoster: finalClassRoster,     // 정화되고 보존된 전체 명단
      students: finalParticipants,       // 정화된 참여자 명단
      teacherId,
      createdAt: serverTimestamp(),
    });

    return { success: true, groupId: newGroupId };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 저장된 모둠 구성 업데이트 (Batched Write)
export const updateStudyGroup = async (teacherId, recordId, newSavedName, groupSnapshot) => {
  try {
    const batch = writeBatch(db);

    // 1. groups_member의 메인 데이터 업데이트
    const mainDocRef = doc(db, 'groups_member', recordId);
    batch.update(mainDocRef, {
      savedName: newSavedName,
      groupSnapshot: groupSnapshot,
      updatedAt: serverTimestamp() // 수정된 시간 기록
    });

    // 2. users/{teacherId}/group_record의 참조 데이터 업데이트
    const userRecordRef = doc(db, 'users', teacherId, 'group_record', recordId);
    batch.update(userRecordRef, {
      savedName: newSavedName,
      className: groupSnapshot.className || '',
      updatedAt: serverTimestamp()
    });

    await batch.commit();
    return { success: true };

  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 토론방의 팀별 학생 닉네임 목록 가져오기
export const getAllDebateStudentNames = async (roomCode, team) => {
  try {
    const teamCollectionRef = collection(db, "debate_group", roomCode, team);
    const q = query(teamCollectionRef, where("isActive", "==", true));
    const querySnapshot = await getDocs(q);
    
    const names = [];
    querySnapshot.forEach((doc) => {
      if (doc.data().nickname) {
        names.push(doc.data().nickname);
      }
    });
    
    // 닉네임 가나다순으로 정렬하여 일관된 순서 보장
    names.sort((a, b) => a.localeCompare(b, 'ko'));
    
    return names;
  } catch (error) {
    return []; // 오류 발생 시 빈 배열 반환
  }
};

// 상대방의 2라운드 주장 가져오기 (무작위 매칭, 인원 불균형 시 최고 점수자와 매칭)
export const getOpponentArgument = async (roomCode, myNickname, myPosition) => {
  try {
    const opponentPosition = myPosition === '찬성' ? '반대' : '찬성';
    const debateDocRef = doc(db, "debate_group", roomCode);
    const debateDocSnap = await getDoc(debateDocRef);

    if (!debateDocSnap.exists()) {
      return { success: false, error: '토론을 찾을 수 없습니다.' };
    }

    const debateData = debateDocSnap.data();
    const rebuttalPairs = debateData.rebuttalPairs;

    if (!rebuttalPairs || rebuttalPairs.length === 0) {
      return { success: false, error: '매칭 정보를 찾는 데 실패했습니다. 새로고침해주세요.' };
    }

    let opponentNickname;
    const mySide = myPosition === '찬성' ? 'pro' : 'con';
    const opponentSide = myPosition === '찬성' ? 'con' : 'pro';

    for (const pair of rebuttalPairs) {
      if (pair[mySide]?.nickname === myNickname) {
        opponentNickname = pair[opponentSide]?.nickname;
        break;
      }
    }

    if (opponentNickname) {
      return await getStudentArgument(roomCode, opponentNickname, opponentPosition, 2);
    }

    return { success: false, error: '상대방을 찾을 수 없습니다. 잠시 후 다시 시도해주세요.' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== 생기부 기본 설정 관리 =====

/**
 * 생기부 기본 설정 저장
 * @param {string} teacherId - 선생님 ID
 * @param {Object} settings - 설정 객체
 * @param {string} settings.recordType - 생기부 유형 (교과세특, 행동특성 및 종합의견 등)
 * @param {string} settings.wordCount - 바이트 수
 * @param {string} settings.activitySelection - 활동 선택 기본값 ('all' or 'none')
 * @returns {Promise<Object>} 성공 여부
 */
export const saveSchoolRecordDefaultSettings = async (teacherId, settings) => {
  try {
    if (!teacherId) {
      throw new Error('teacherId가 필요합니다.');
    }

    const teacherDocRef = doc(teachClassDB, 'school-record', teacherId);
    
    await setDoc(teacherDocRef, {
      defaultSettings: {
        recordType: settings.recordType || '교과세특',
        wordCount: settings.wordCount || '1000',
        activitySelection: settings.activitySelection || 'all'
      },
      lastUpdated: serverTimestamp()
    }, { merge: true });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * 생기부 기본 설정 불러오기
 * @param {string} teacherId - 선생님 ID
 * @returns {Promise<Object>} 설정 객체 또는 기본값
 */
export const getSchoolRecordDefaultSettings = async (teacherId) => {
  try {
    if (!teacherId) {
      throw new Error('teacherId가 필요합니다.');
    }

    const teacherDocRef = doc(teachClassDB, 'school-record', teacherId);
    const docSnap = await getDoc(teacherDocRef);

    if (docSnap.exists() && docSnap.data().defaultSettings) {
      const settings = docSnap.data().defaultSettings;
      return {
        success: true,
        data: {
          recordType: settings.recordType || '교과세특',
          wordCount: settings.wordCount || '1000',
          activitySelection: settings.activitySelection || 'all'
        }
      };
    }

    // DB에 저장된 설정이 없으면 기본값 반환
    return {
      success: true,
      data: {
        recordType: '교과세특',
        wordCount: '1000',
        activitySelection: 'all'
      },
      isDefault: true
    };
  } catch (error) {
    // 오류 발생 시에도 기본값 반환
    return {
      success: true,
      data: {
        recordType: '교과세특',
        wordCount: '1000',
        activitySelection: 'all'
      },
      isDefault: true,
      error: error.message
    };
  }
};

// ===== 생기부 임시저장 관리 =====

/**
 * 생기부 임시저장 생성
 * @param {string} teacherId - 선생님 ID
 * @param {string} draftTitle - 임시저장 제목
 * @param {Array} students - 학생 목록 (selectedStudents 배열)
 * @returns {Promise<Object>} 성공 여부 및 draftId
 */
export const saveSchoolRecordDraft = async (teacherId, draftTitle, students) => {
  try {
    if (!teacherId || !draftTitle || !students || students.length === 0) {
      return { success: false, error: '필수 정보가 누락되었습니다.' };
    }

    const draftId = `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const draftRef = doc(teachClassDB, 'school-record', teacherId, 'drafts', draftId);

    // 학생 데이터 변환 (selectedStudents -> DB 형식)
    const studentsData = students.map(student => ({
      name: student.name,
      studentId: student.id,
      classId: student.classId,
      className: student.class,
      generationOption: student.settingMode, // 'default' or 'custom'
      // 🔥 모든 활동과 체크 상태를 함께 저장
      activities: student.activityRecords
        ? student.activityRecords.map(a => ({
            id: a.id,
            title: a.title,
            checked: a.checked,
            isManual: a.isManual || false // 수동 입력 여부
          }))
        : [],
      // custom 설정인 경우에만 포함
      ...(student.settingMode === 'custom' && {
        recordType: student.recordType,
        wordCount: student.wordCount
      })
    }));

    const draftData = {
      draftTitle,
      students: studentsData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(draftRef, draftData);

    return { success: true, draftId };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * 생기부 임시저장 목록 불러오기
 * @param {string} teacherId - 선생님 ID
 * @returns {Promise<Object>} 임시저장 목록
 */
export const getSchoolRecordDrafts = async (teacherId) => {
  try {
    if (!teacherId) {
      return { success: false, error: 'teacherId가 필요합니다.' };
    }

    const draftsRef = collection(teachClassDB, 'school-record', teacherId, 'drafts');
    const q = query(draftsRef, orderBy('updatedAt', 'desc'));
    const querySnapshot = await getDocs(q);

    const drafts = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      drafts.push({
        id: doc.id,
        draftTitle: data.draftTitle,
        studentCount: data.students?.length || 0,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt
      });
    });

    return { success: true, data: drafts };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * 특정 임시저장 상세 불러오기
 * @param {string} teacherId - 선생님 ID
 * @param {string} draftId - 임시저장 ID
 * @returns {Promise<Object>} 임시저장 상세 데이터
 */
export const getSchoolRecordDraft = async (teacherId, draftId) => {
  try {
    if (!teacherId || !draftId) {
      return { success: false, error: '필수 정보가 누락되었습니다.' };
    }

    const draftRef = doc(teachClassDB, 'school-record', teacherId, 'drafts', draftId);
    const docSnap = await getDoc(draftRef);

    if (!docSnap.exists()) {
      return { success: false, error: '임시저장을 찾을 수 없습니다.' };
    }

    const data = docSnap.data();
    return {
      success: true,
      data: {
        id: docSnap.id,
        draftTitle: data.draftTitle,
        students: data.students,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt
      }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * 임시저장 업데이트
 * @param {string} teacherId - 선생님 ID
 * @param {string} draftId - 임시저장 ID
 * @param {string} draftTitle - 새 제목
 * @param {Array} students - 학생 목록
 * @returns {Promise<Object>} 성공 여부
 */
export const updateSchoolRecordDraft = async (teacherId, draftId, draftTitle, students) => {
  try {
    if (!teacherId || !draftId) {
      return { success: false, error: '필수 정보가 누락되었습니다.' };
    }

    const draftRef = doc(teachClassDB, 'school-record', teacherId, 'drafts', draftId);

    // 학생 데이터 변환
    const studentsData = students.map(student => ({
      name: student.name,
      studentId: student.id,
      classId: student.classId,
      className: student.class,
      generationOption: student.settingMode,
      // 🔥 모든 활동과 체크 상태를 함께 저장
      activities: student.activityRecords
        ? student.activityRecords.map(a => ({
            id: a.id,
            title: a.title,
            checked: a.checked,
            isManual: a.isManual || false
          }))
        : [],
      ...(student.settingMode === 'custom' && {
        recordType: student.recordType,
        wordCount: student.wordCount
      })
    }));

    await updateDoc(draftRef, {
      draftTitle,
      students: studentsData,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * 임시저장 삭제
 * @param {string} teacherId - 선생님 ID
 * @param {string} draftId - 임시저장 ID
 * @returns {Promise<Object>} 성공 여부
 */
export const deleteSchoolRecordDraft = async (teacherId, draftId) => {
  try {
    if (!teacherId || !draftId) {
      return { success: false, error: '필수 정보가 누락되었습니다.' };
    }

    const draftRef = doc(teachClassDB, 'school-record', teacherId, 'drafts', draftId);
    await deleteDoc(draftRef);

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== 생기부 일일 사용 횟수 관리 =====

/**
 * 일일 생기부 사용 횟수 체크
 * @param {string} teacherId - 선생님 ID
 * @param {boolean} isPro - 프로 계정 여부
 * @param {boolean} isAdmin - 관리자 계정 여부
 * @returns {Promise<Object>} 허용 여부 및 남은 횟수
 */
export const checkDailyUsageLimit = async (teacherId, isPro = false, isAdmin = false) => {
  try {
    // 프로 계정 또는 관리자는 무제한
    if (isPro || isAdmin) {
      return { 
        success: true,
        allowed: true, 
        current: 0,
        remaining: Infinity,
        isPro,
        isAdmin
      };
    }
    
    // 오늘 날짜 (KST 기준)
    const today = new Date().toLocaleDateString('ko-KR', { 
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\. /g, '-').replace('.', ''); // YYYY-MM-DD 형식
    
    const usageDocRef = doc(teachClassDB, 'school-record', teacherId, 'dailyUsage', today);
    const docSnap = await getDoc(usageDocRef);
    
    const currentCount = docSnap.exists() ? (docSnap.data().count || 0) : 0;
    const remaining = Math.max(0, 3 - currentCount);
    
    return {
      success: true,
      allowed: currentCount < 3,
      current: currentCount,
      remaining,
      limit: 3,
      isPro: false,
      isAdmin: false
    };
  } catch (error) {
    return { 
      success: false, 
      error: error.message,
      allowed: false,
      current: 0,
      remaining: 0
    };
  }
};

/**
 * 일일 생기부 사용 횟수 증가
 * @param {string} teacherId - 선생님 ID
 * @param {number} studentCount - 생성한 학생 수
 * @returns {Promise<Object>} 성공 여부
 */
export const incrementDailyUsage = async (teacherId, studentCount = 1) => {
  try {
    if (!teacherId || studentCount <= 0) {
      return { success: false, error: '유효하지 않은 파라미터입니다.' };
    }
    
    // 오늘 날짜 (KST 기준)
    const today = new Date().toLocaleDateString('ko-KR', { 
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\. /g, '-').replace('.', ''); // YYYY-MM-DD 형식
    
    const usageDocRef = doc(teachClassDB, 'school-record', teacherId, 'dailyUsage', today);
    
    // 문서 생성 또는 카운트 증가
    await setDoc(usageDocRef, {
      count: increment(studentCount),
      lastUpdated: serverTimestamp(),
      date: today
    }, { merge: true });
    
    return { success: true, date: today, incrementBy: studentCount };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * 마인드맵 학생 활동 기록 추가/업데이트
 */
export const addMindMapActivityRecord = async (classId, studentId, activityId, recordData) => {
  try {
    const recordRef = doc(teachClassDB, 'classes', classId, 'students', studentId, 'activityRecords', activityId);
    await setDoc(recordRef, recordData, { merge: true });
    
    // 🚀 성능 최적화: 해당 학생의 기록 캐시 무효화
    invalidateStudentRecordsCache(studentId);
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== 생기부 온라인 저장 =====

/**
 * 학생의 저장된 생기부 목록 가져오기
 * @param {string} classId - 학급 ID
 * @param {string} studentId - 학생 ID
 * @returns {Promise<Object>} 성공 여부 및 생기부 목록
 */
export const getStudentSchoolRecords = async (classId, studentId) => {
  try {
    if (!classId || !studentId) {
      return { success: false, error: '필수 정보가 누락되었습니다.' };
    }

    const recordsRef = collection(teachClassDB, 'classes', classId, 'students', studentId, 'schoolRecords');
    const q = query(recordsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const records = [];
    snapshot.forEach(doc => {
      records.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return { success: true, records, count: records.length };
  } catch (error) {
    return { success: false, error: error.message, records: [], count: 0 };
  }
};

/**
 * 개별 생기부 온라인 저장 (덮어쓰기 지원)
 * @param {string} classId - 학급 ID
 * @param {string} studentId - 학생 ID
 * @param {Object} recordData - 생기부 데이터
 * @param {string} recordData.studentName - 학생 이름
 * @param {string} recordData.studentNumber - 학생 번호
 * @param {string} recordData.className - 학급명
 * @param {string} recordData.recordType - 생기부 유형
 * @param {string} recordData.content - 생기부 내용
 * @param {number} recordData.wordCount - 바이트 수
 * @param {number} recordData.activityCount - 활동 수
 * @param {Array<string>} recordData.activities - 활동 목록
 * @param {string} recordData.status - 상태 (success/warning)
 * @param {Array} recordData.warnings - 경고 목록
 * @param {string} overwriteRecordId - 덮어쓸 기존 레코드 ID (선택)
 * @returns {Promise<Object>} 성공 여부 및 recordId
 */
export const saveSchoolRecordOnline = async (classId, studentId, recordData, overwriteRecordId = null) => {
  try {
    if (!classId || !studentId || !recordData) {
      return { success: false, error: '필수 정보가 누락되었습니다.' };
    }

    // recordId 생성 (덮어쓰기인 경우 기존 ID 사용)
    const recordId = overwriteRecordId || `record_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // classes/{classId}/students/{studentId}/schoolRecords/{recordId}
    const recordRef = doc(teachClassDB, 'classes', classId, 'students', studentId, 'schoolRecords', recordId);
    
    const schoolRecordData = {
      recordId,
      studentId,
      studentName: recordData.studentName,
      studentNumber: recordData.studentNumber || null,
      className: recordData.className,
      recordType: recordData.recordType || '교과세특',
      content: recordData.content || '',
      wordCount: recordData.wordCount || 0,
      activityCount: recordData.activityCount || 0,
      activities: recordData.activities || [],
      status: recordData.status || 'success',
      warnings: recordData.warnings || [],
      createdAt: overwriteRecordId ? recordData.createdAt : serverTimestamp(), // 덮어쓰기 시 생성시간 유지
      updatedAt: serverTimestamp()
    };
    
    await setDoc(recordRef, schoolRecordData);
    
    return { success: true, recordId, isOverwrite: !!overwriteRecordId };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * 생기부 업데이트
 * @param {string} classId - 학급 ID
 * @param {string} studentId - 학생 ID
 * @param {string} recordId - 생기부 ID
 * @param {Object} updateData - 업데이트할 데이터
 * @returns {Promise<Object>} 성공 여부
 */
export const updateSchoolRecord = async (classId, studentId, recordId, updateData) => {
  try {
    if (!classId || !studentId || !recordId || !updateData) {
      return { success: false, error: '필수 정보가 누락되었습니다.' };
    }

    const recordRef = doc(teachClassDB, 'classes', classId, 'students', studentId, 'schoolRecords', recordId);
    
    await updateDoc(recordRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * 생기부 삭제
 * @param {string} classId - 학급 ID
 * @param {string} studentId - 학생 ID
 * @param {string} recordId - 생기부 ID
 * @returns {Promise<Object>} 성공 여부
 */
export const deleteSchoolRecord = async (classId, studentId, recordId) => {
  try {
    if (!classId || !studentId || !recordId) {
      return { success: false, error: '필수 정보가 누락되었습니다.' };
    }

    const recordRef = doc(teachClassDB, 'classes', classId, 'students', studentId, 'schoolRecords', recordId);
    await deleteDoc(recordRef);
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * 전체 생기부 일괄 온라인 저장
 * @param {Array<Object>} records - 생기부 목록
 * @returns {Promise<Object>} 성공 여부 및 저장된 개수
 */
export const saveBulkSchoolRecordsOnline = async (records) => {
  try {
    if (!records || records.length === 0) {
      return { success: false, error: '저장할 생기부가 없습니다.' };
    }

    const batch = writeBatch(teachClassDB);
    const savedRecordIds = [];

    for (const record of records) {
      if (!record.classId || !record.id) {
        continue;
      }

      const recordId = `record_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const recordRef = doc(teachClassDB, 'classes', record.classId, 'students', record.id, 'schoolRecords', recordId);
      
      const schoolRecordData = {
        recordId,
        studentId: record.id,
        studentName: record.name,
        studentNumber: record.number || null,
        className: record.class,
        recordType: record.recordType || '교과세특',
        content: record.content || '',
        wordCount: record.wordCount || 0,
        activityCount: record.activityCount || 0,
        activities: record.activities || [],
        status: record.status || 'success',
        warnings: record.warnings || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      batch.set(recordRef, schoolRecordData);
      savedRecordIds.push(recordId);
    }

    await batch.commit();
    
    return { 
      success: true, 
      savedCount: savedRecordIds.length,
      recordIds: savedRecordIds
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== 🚀 생기부 히스토리 관리 ====================

/**
 * 생기부 생성 히스토리 저장
 * @param {string} classId - 학급 ID
 * @param {Array<Object>} results - 생성된 생기부 결과 목록
 * @param {string} recordType - 생기부 유형 (교과세특, 자율/봉사/진로 등)
 * @returns {Promise<Object>} 저장 결과
 */
export const createSchoolRecordHistory = async (classId, results, recordType = '교과세특') => {
  try {
    if (!classId || !results || results.length === 0) {
      return { success: false, error: '필수 정보가 누락되었습니다.' };
    }

    const historyId = `history_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const historyRef = doc(teachClassDB, 'classes', classId, 'schoolRecordHistory', historyId);

    // 학생별 생기부 정보 요약
    const studentRecords = results.map(result => ({
      studentId: result.id,
      studentName: result.name,
      studentNumber: result.number || null,
      content: result.content || '',
      wordCount: result.wordCount || 0,
      activityCount: result.activityCount || 0,
      activities: result.activities || [],
      status: result.status || 'success',
      warnings: result.warnings || []
    }));

    const historyData = {
      historyId,
      classId,
      recordType,
      studentCount: results.length,
      students: studentRecords,
      createdAt: serverTimestamp()
    };

    await setDoc(historyRef, historyData);

    return { success: true, historyId };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * 학급의 모든 생기부 히스토리 가져오기
 * @param {string} classId - 학급 ID
 * @returns {Promise<Object>} 히스토리 목록
 */
export const getAllSchoolRecordHistoryForClass = async (classId) => {
  try {
    if (!classId) {
      return { success: false, error: '학급 ID가 필요합니다.' };
    }

    const historyCollectionRef = collection(teachClassDB, 'classes', classId, 'schoolRecordHistory');
    const q = query(historyCollectionRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);

    const history = [];
    querySnapshot.forEach((doc) => {
      history.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, data: history };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * 특정 생기부 히스토리 상세 정보 가져오기
 * @param {string} classId - 학급 ID
 * @param {string} historyId - 히스토리 ID
 * @returns {Promise<Object>} 히스토리 상세 정보
 */
export const getSchoolRecordHistoryDetails = async (classId, historyId) => {
  try {
    if (!classId || !historyId) {
      return { success: false, error: '학급 ID와 히스토리 ID가 필요합니다.' };
    }

    const historyRef = doc(teachClassDB, 'classes', classId, 'schoolRecordHistory', historyId);
    const historySnap = await getDoc(historyRef);

    if (!historySnap.exists()) {
      return { success: false, error: '히스토리를 찾을 수 없습니다.' };
    }

    return { success: true, data: { id: historySnap.id, ...historySnap.data() } };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * 생기부 히스토리 삭제
 * @param {string} classId - 학급 ID
 * @param {string} historyId - 히스토리 ID
 * @returns {Promise<Object>} 삭제 결과
 */
export const deleteSchoolRecordHistory = async (classId, historyId) => {
  try {
    if (!classId || !historyId) {
      return { success: false, error: '학급 ID와 히스토리 ID가 필요합니다.' };
    }

    // 생기부 히스토리 문서 삭제
    const historyRef = doc(teachClassDB, 'classes', classId, 'schoolRecordHistory', historyId);
    await deleteDoc(historyRef);
    
    return { 
      success: true, 
      message: '생기부 생성 기록이 삭제되었습니다.'
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * 최근 생기부 히스토리 가져오기 (제한된 개수)
 * @param {string} classId - 학급 ID
 * @param {number} limitCount - 가져올 개수
 * @returns {Promise<Object>} 최근 히스토리 목록
 */
export const getRecentSchoolRecordHistoryForClass = async (classId, limitCount = 10) => {
  try {
    if (!classId) {
      return { success: false, error: '학급 ID가 필요합니다.' };
    }

    const historyCollectionRef = collection(teachClassDB, 'classes', classId, 'schoolRecordHistory');
    const q = query(historyCollectionRef, orderBy('createdAt', 'desc'), limit(limitCount));
    const querySnapshot = await getDocs(q);

    const history = [];
    querySnapshot.forEach((doc) => {
      history.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, data: history };
  } catch (error) {
    return { success: false, error: error.message };
  }
};